import axiosApiInstance from "../../../api/axiosClient";
import {atomWithQuery, atomWithSuspenseQuery} from "jotai-tanstack-query";
import {atomWithHash} from "jotai-location";
import {atom} from "jotai";
import { atomFamily } from "jotai/utils";

// Tag filters atom - array of selected tag values
export const tagFiltersAtom = atomWithHash<string[]>('tagFilters', []);

// Persistent selection atom - stores selected creator IDs across filter changes
export const selectedCreatorIdsAtom = atom<number[]>([]);

// Sort atoms
export const sortFieldAtom = atomWithHash<string | undefined>('sortField', undefined);
export const sortOrderAtom = atomWithHash<'ascend' | 'descend' | undefined>('sortOrder', undefined);

// Filter atoms
export const identityFilterAtom = atomWithHash<string[] | undefined>('identityFilter', undefined);

// Follower range filters - [min, max] for each platform
export const youtubeFollowersRangeAtom = atomWithHash<[number, number]>('youtubeFollowers', [0, 1000000]);
export const instagramFollowersRangeAtom = atomWithHash<[number, number]>('instagramFollowers', [0, 1000000]);
export const tiktokFollowersRangeAtom = atomWithHash<[number, number]>('tiktokFollowers', [0, 1000000]);

// Has-contact filter: 'all' (no filter) | 'has' | 'missing'
export type HasContactFilter = 'all' | 'has' | 'missing';
export const hasContactFilterAtom = atomWithHash<HasContactFilter>('hasContact', 'all');

// /api/creators/items
// export const creatorsAtom = atomWithSuspenseQuery<API.DataResponse<API.Creators.Creator[]>>((get) => ({
export const creatorsAtom = atomWithQuery<API.DataResponse<API.Creators.Creator[]>>((get) => ({
    // const response = await axiosApiInstance.get(`/api/creators/items`);
    // // console.log(response.data)
    // return response.data;
    queryKey: [
        'creators',
        'items',
        get(pageAtom),
        get(pageSizeAtom),
        get(filterSearchAtom),
        get(tagFiltersAtom),
        get(sortFieldAtom),
        get(sortOrderAtom),
        get(identityFilterAtom),
        get(youtubeFollowersRangeAtom),
        get(instagramFollowersRangeAtom),
        get(tiktokFollowersRangeAtom),
        get(hasContactFilterAtom)],
    queryFn: async () => {
        const hasContact = get(hasContactFilterAtom);
        const response = await axiosApiInstance.get(`/api/creators/items`, {
            params: {
                page: get(pageAtom),
                page_size: get(pageSizeAtom),
                search: get(filterSearchAtom),
                tag_filters: get(tagFiltersAtom),
                sort_field: get(sortFieldAtom),
                sort_order: get(sortOrderAtom),
                main_platform_id: get(identityFilterAtom),
                youtube_followers_min: get(youtubeFollowersRangeAtom)[0],
                youtube_followers_max: get(youtubeFollowersRangeAtom)[1],
                instagram_followers_min: get(instagramFollowersRangeAtom)[0],
                instagram_followers_max: get(instagramFollowersRangeAtom)[1],
                tiktok_followers_min: get(tiktokFollowersRangeAtom)[0],
                tiktok_followers_max: get(tiktokFollowersRangeAtom)[1],
                has_contact: hasContact === 'has' ? 'true' : hasContact === 'missing' ? 'false' : undefined,
            }
        });
        return response.data;
    }

}));

export const filterSearchAtom = atomWithHash<string | undefined>('filterSearch', undefined);


// /api/creators/categories/
export const creatorCategoriesAtom = atomWithSuspenseQuery<API.Creators.CreatorContentCategory[]>((get) => ({
    // const response = await axiosApiInstance.get(`/api/creators/categories/`);
    // console.log(response.data)
    // return response.data;
    queryKey: ['creators', 'categories'],
    queryFn: async () => {
        const response = await axiosApiInstance.get(`/api/creators/categories/`);
        return response.data;
    }
}));

// pagination
export const pageAtom = atomWithHash<number>('creatorsCurrent', 1);
export const pageSizeAtom = atomWithHash<number>('creatorsPageSize', 7);

export enum CreatorGroupBy {
    Creator = 'creator',
    YoutubeAccount = 'youtube_account',
    InstagramAccount = 'instagram_account',
    TiktokAccount = 'tiktok_account',
}

export const groupByAtom = atomWithHash<CreatorGroupBy>('groupBy', CreatorGroupBy.Creator);


export const creatorsAccountsByPlatformAtom = atomWithQuery<API.DataResponse<API.Creators.CreatorWithAccount[]>>((get) => ({
    // const response = await axiosApiInstance.get(`/api/creators/items`);
    // // console.log(response.data)
    // return response.data;
    queryKey: ['creators', 'accounts', 'by_platform', get(groupByAtom), get(pageAtom), get(pageSizeAtom), get(filterSearchAtom), get(tagFiltersAtom), get(youtubeFollowersRangeAtom), get(instagramFollowersRangeAtom), get(tiktokFollowersRangeAtom)],
    queryFn: async () => {
        let url;
        if (get(groupByAtom) === CreatorGroupBy.YoutubeAccount) {
            url = `/api/creators/accounts/by_platform/1`;
        } else if (get(groupByAtom) === CreatorGroupBy.InstagramAccount) {
            url = `/api/creators/accounts/by_platform/2`;
        } else if (get(groupByAtom) === CreatorGroupBy.TiktokAccount) {
            url = `/api/creators/accounts/by_platform/3`;
        } else {
            return [];
        }
        const response = await axiosApiInstance.get(url, {
            params: {
                page: get(pageAtom),
                page_size: get(pageSizeAtom),
                search: get(filterSearchAtom),
                tag_filters: get(tagFiltersAtom),
                youtube_followers_min: get(youtubeFollowersRangeAtom)[0],
                youtube_followers_max: get(youtubeFollowersRangeAtom)[1],
                instagram_followers_min: get(instagramFollowersRangeAtom)[0],
                instagram_followers_max: get(instagramFollowersRangeAtom)[1],
                tiktok_followers_min: get(tiktokFollowersRangeAtom)[0],
                tiktok_followers_max: get(tiktokFollowersRangeAtom)[1],
            }
        });
        return response.data;
    }

}));



// family of atoms (because we need to pass id to the atom from the component)
export const creatorTagsAtomById = atomFamily((id) => {
    // console.log('creatorTagsAtomById', id);
    return atomWithSuspenseQuery((get) => ({
        queryKey: ['creators', 'edit', 'tags', id],
        queryFn: async () => {
            const response = await axiosApiInstance.get(`/api/creators/tags/${id}`);
            return response.data;
        }
    }))
})

const AI_DESCRIPTION_POLL_MS = 3000;
const AI_DESCRIPTION_POLL_WINDOW_MS = 5 * 60 * 1000;

function shouldPollForAIDescription(creator: API.Creators.Creator | undefined): number | false {
    if (!creator?.id || creator.ai_description?.trim()) return false;
    const createdAt = creator.created_at ? new Date(creator.created_at).getTime() : 0;
    if (!createdAt || Date.now() - createdAt > AI_DESCRIPTION_POLL_WINDOW_MS) return false;
    return AI_DESCRIPTION_POLL_MS;
}

export const creatorAtom = atomFamily((id: string | undefined) => {
    return atomWithSuspenseQuery<API.Creators.Creator | undefined>((get) => ({
        queryKey: ['creators', id],
        queryFn: async (): Promise<API.Creators.Creator | undefined> => {
            if (!id) return;
            const response = await axiosApiInstance.get(`/api/creators/items/${id}`);
            return response.data;
        },
        refetchInterval: (query) => shouldPollForAIDescription(query.state.data),
    }))
} )

// Tag counts atom - gets total counts of creators for each tag value (not filtered)
export const tagCountsAtom = atomWithQuery<{tag_category_value_id: number, count: number}[]>((get) => ({
    queryKey: ['creators', 'tags', 'counts', 
        get(pageAtom), 
        get(pageSizeAtom), 
        get(filterSearchAtom), 
        get(tagFiltersAtom), 
        get(identityFilterAtom),
        get(youtubeFollowersRangeAtom), 
        get(instagramFollowersRangeAtom), 
        get(tiktokFollowersRangeAtom), 
        get(creatorsAtom)],
    queryFn: async () => {
        const creatorsResp = get(creatorsAtom);
        const visibleIds = (creatorsResp && creatorsResp.data && Array.isArray(creatorsResp.data.data)) ? creatorsResp.data.data.map((c: any) => c.id) : [];

        const params: any = {
            search: get(filterSearchAtom),
            tag_filters: get(tagFiltersAtom),
            main_platform_id: get(identityFilterAtom),
            youtube_followers_min: get(youtubeFollowersRangeAtom)[0],
            youtube_followers_max: get(youtubeFollowersRangeAtom)[1],
            instagram_followers_min: get(instagramFollowersRangeAtom)[0],
            instagram_followers_max: get(instagramFollowersRangeAtom)[1],
            tiktok_followers_min: get(tiktokFollowersRangeAtom)[0],
            tiktok_followers_max: get(tiktokFollowersRangeAtom)[1],
        };

        if (visibleIds.length > 0) {
            params.visible_creator_ids = visibleIds;
        }

        const response = await axiosApiInstance.get('/api/creators/tags/counts', { params });
        return response.data;
    }
}));